import { photoPath } from '@/domain';

/**
 * The TanStack Query key registry, section 8.2. One place, so invalidation is
 * not guesswork and a typo does not silently create a second cache entry.
 */
export const keys = {
  session: ['session'] as const,
  board: () => ['board'] as const,
  kits: {
    all: ['kits'] as const,
    list: (filter: unknown) => ['kits', 'list', filter] as const,
    byId: (id: string) => ['kits', 'detail', id] as const,
    history: (id: string) => ['kits', 'history', id] as const,
    // A case's Lab Notes, under its detail key so anything that refreshes the case refreshes them.
    notes: (id: string) => ['kits', 'detail', id, 'notes'] as const,
    // Who may be given the kit. Under the kits prefix, so a list refresh takes it too.
    assignees: (id: string, stage?: string) => ['kits', 'assignees', id, stage ?? ''] as const,
    /**
     * What this person has running. Section 1.5 of the request of 20 September
     * 2026: the device keeps no count, so a reopened browser reads its clocks
     * from here. Under the kits prefix, so anything that moves a kit refetches it.
     */
    running: () => ['kits', 'running'] as const,
    photos: (id: string) => ['kits', 'photos', id] as const,
    // The proofs of finished work, apart from the case photographs.
    workProofs: (id: string) => ['kits', 'work-proof', id] as const,
    // Keyed by the photograph's URL, not the kit's id: the bytes belong to one
    // photograph and the same image is shown from more than one screen.
    // Without the signature on it: every list comes back signed afresh, and a
    // key that held the signature fetched every photograph again after every
    // refresh. The request of 5 October 2026 (third), section 6.
    photoBlob: (url: string) => ['kits', 'photo-blob', photoPath(url)] as const,
  },
  orders: {
    all: ['orders'] as const,
    byNo: (jo: string) => ['orders', jo] as const,
    // Under the orders prefix, so an edit or a payment refreshes an open receipt.
    receipt: (jo: string) => ['orders', jo, 'receipt'] as const,
    // What Accounts review on a case. Under the orders prefix too, so an edit, a
    // review or one taken back refreshes an open changes sheet.
    accountsReview: (jo: string) => ['orders', jo, 'accounts-review'] as const,
    // The waiting cases that need no review. Under the prefix too: a review or an edit changes it.
    reviewCleanup: () => ['orders', 'review-cleanup'] as const,
    /*
     * The money of a case, of a doctor and of the lab. The request of
     * 8 October 2026 (fourth). All three under the orders prefix: a payment, a
     * discount, an edit or a hold changes what at least one of them says.
     */
    paymentSummary: (jo: string) => ['orders', jo, 'payment-summary'] as const,
    statement: (doctorId: string) => ['orders', 'finance', 'statement', doctorId] as const,
    financeOverview: () => ['orders', 'finance', 'overview'] as const,
    /*
     * The lab's request of 17 October 2026: the cases in hand counted, and a
     * doctor's own prices. Under the prefix: a case moving, or a payment,
     * changes the first, and a price changed the second.
     */
    counts: () => ['orders', 'counts'] as const,
    doctorPrices: (doctorId: string) => ['orders', 'finance', 'doctor-prices', doctorId] as const,
    financeSettings: () => ['orders', 'finance', 'settings'] as const,
  },
  archive: (filter: unknown) => ['archive', filter] as const,
  /*
   * Doctors and patients. The request of 6 October 2026 (second), items 4 and
   * 5. Each has its lists under one prefix and each record under another, so a
   * deleted record's lists can be read again without asking for the record
   * that is gone. A record's patients and cases sit under the record.
   */
  doctors: {
    all: ['doctors'] as const,
    lists: ['doctors', 'list'] as const,
    list: (filter: unknown) => ['doctors', 'list', filter] as const,
    // What the intake form's doctor field is suggesting, by what was typed.
    search: (text: string) => ['doctors', 'list', 'search', text] as const,
    // Every record, without the lists: what a merge or a delete reads again.
    details: ['doctors', 'detail'] as const,
    byId: (id: string) => ['doctors', 'detail', id] as const,
    // Every list of one doctor's patients, and one of them by what it was asked for.
    patientLists: (id: string) => ['doctors', 'detail', id, 'patients'] as const,
    patients: (id: string, filter: unknown) =>
      ['doctors', 'detail', id, 'patients', filter] as const,
    cases: (id: string, page: unknown) => ['doctors', 'detail', id, 'cases', page] as const,
    // A doctor's possible duplicates. The request of 6 October 2026 (third), item 1.
    similar: (id: string) => ['doctors', 'detail', id, 'similar'] as const,
    // The counts above the list, and what converting the old numbers and
    // addresses would do. Under the doctors prefix, so anything that reads the
    // doctors again reads these again. The request of 6 October 2026 (fifth).
    summary: ['doctors', 'summary'] as const,
    conversion: ['doctors', 'conversion'] as const,
  },
  patients: {
    all: ['patients'] as const,
    lists: ['patients', 'list'] as const,
    list: (filter: unknown) => ['patients', 'list', filter] as const,
    // What the intake form's patient field is suggesting, by what was typed.
    search: (text: string) => ['patients', 'list', 'search', text] as const,
    byId: (id: string) => ['patients', 'detail', id] as const,
    cases: (id: string, page: unknown) => ['patients', 'detail', id, 'cases', page] as const,
    summary: ['patients', 'summary'] as const,
  },
  /*
   * Geography for an address. The request of 6 October 2026 (fifth), section
   * 2. The countries never change while the page is open; the regions hardly
   * ever; the cities and the areas include what doctors' addresses say, so a
   * saved address reads them again.
   */
  geo: {
    countries: ['geo', 'countries'] as const,
    regions: (country: string) => ['geo', 'regions', country] as const,
    // Every city and area search, to read them again after an address is saved.
    places: ['geo', 'places'] as const,
    cities: (country: string, region: string, text: string) =>
      ['geo', 'places', 'cities', country, region, text] as const,
    areas: (country: string, city: string, text: string) =>
      ['geo', 'places', 'areas', country, city, text] as const,
  },
  // The telephone library, loaded once.
  phoneBook: ['phone-book'] as const,
  reports: (kind: string, range: string, filter?: unknown) =>
    ['reports', kind, range, filter] as const,
  activity: (filter: unknown) => ['activity', filter] as const,
  // Under the same prefix as the whole log, so anything that invalidates the log
  // refreshes every case history on screen with it.
  caseActivity: (jobOrderNo: string) => ['activity', 'case', jobOrderNo] as const,
  // The lists and the count are under this, so reading one refreshes the bell with it.
  notifications: ['notifications'] as const,
  // What the person chose about their own notifications, and management's settings.
  myNotifications: ['preferences', 'notifications'] as const,
  notificationSettings: ['notification-settings'] as const,
  // Every table the person laid out their own way, in one list.
  tableLayouts: ['preferences', 'tables'] as const,
  users: ['users'] as const,
  userCases: (id: string, filter: unknown) => ['users', id, 'cases', filter] as const,
  access: ['access-matrix'] as const,
  // Under the users prefix, so saving a person refreshes the team list's pill with it.
  userAccess: (id: string) => ['users', id, 'access'] as const,
  // Under the users prefix too: a change to the person refreshes their profile.
  profile: (id: string, range: string) => ['users', id, 'profile', range] as const,
  catalog: ['catalog'] as const,
  // The installed app's colours and icon set. Request of 5 October 2026.
  pwaSettings: ['pwa-settings'] as const,
  /*
   * The printed sheet. It is cached for five minutes so a sheet opens from a
   * cold network, which means anything that shows on paper must invalidate
   * it: otherwise a case marked urgent and reprinted comes out without the flag.
   */
  print: (kitId: string) => ['print', kitId] as const,
  printAll: ['print'] as const,
  stages: ['stages'] as const,
};
